import moment from 'moment';
import { useHistory } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import React, { useState, useEffect } from 'react';
import { CircularProgress } from "@material-ui/core";
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody, ModalHeader, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { TextField, FormControl, Select, MenuItem, Menu, Checkbox, ListItemText, Popover, Dialog } from '@mui/material';

import './index.css';
import BoxView from './BoxView';
import constants from '../../constants';
import 'react-toastify/dist/ReactToastify.css';
import EmptyAgentState from './EmptyAgentState';
import { getAllObjectTags } from '../../store/Objects';
import TitleRoute from '../../components/titleRoute.js';
import ViewStateManager from '../../utils/viewStateManager';
import DefaultAvatar from '../../static/img/agent-img-3.png';
import { getWorkspacePath } from '../../utils/workspaceUtils';
import TagsManager from '../../components/Tags/TagsManager.js';
import TableAnimationFiles from '../../components/lottiefilesAnimation/TableAnimationFiles.js';
import { getAllAgents, deleteAgent, setNewAgentModal, cloneAgent, } from '../../store/Agent';

const Agents = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  // Load saved view state
  const savedViewState = ViewStateManager.getAgentView();
  const [isTableView, setTableView] = useState(savedViewState['isTableView']);

  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusDropdownAnchor, setStatusDropdownAnchor] = useState(null);
  const [tagFilterAnchor, setTagFilterAnchor] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});
  const [selectedTagsList, setSelectedTagsList] = useState([]); // For TagsManager filter mode
  const [agentTypes, setAgentTypes] = useState([]);
  const [agentStatuses, setAgentStatuses] = useState([]);
  const [isTagsModal, setTagsModal] = useState(false);
  const { allAgents, newAgentModal, isLoading } = useSelector(state => state['Agent']);
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const { agents: agentsState } = useSelector(state => state['Objects']);
  const allTags = agentsState?.allTags || [];

  const [tagsManagerOpen, setTagsManagerOpen] = useState(false);

  // Save view state when it changes
  useEffect(() => { ViewStateManager.setAgentView({ isTableView }) }, [isTableView]);
  const handleClick = (event) => setStatusDropdownAnchor(event.currentTarget);
  const handleClose = () => setStatusDropdownAnchor(null);
  const handleClickEditTags = () => {
    setTagsModal(true);
  };

  const handleOpenTagsManager = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget); // jis button pe click hua usi ko anchor bana diya
  };

  const handleCloseTagsManager = () => {
    setAnchorEl(null);
  };

  const toggleValue = (value) => setSelectedValues((prev) => ({ ...prev, [value]: !prev[value] }));
  const handleClickTagFilter = (event) => setTagFilterAnchor(event.currentTarget);
  const handleCloseTagFilter = () => setTagFilterAnchor(null);
  const handleTagsFilterChange = (selectedTagNames) => {
    setSelectedTagsList(selectedTagNames);
  };

  const [isDelete, setDelete] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    dispatch(getAllAgents());
    dispatch(getAllObjectTags({ objectType: 'agents' }));
  }, []);

  // Extract distinct agent types and statuses when agents data changes
  useEffect(() => {
    if (allAgents && allAgents.length > 0) {
      // Extract types
      const types = [...new Set(allAgents.map(agent => agent['type']).filter(type => type))];
      setAgentTypes(types.sort());

      // Compute and extract statuses
      const statuses = [...new Set(allAgents.map(agent => {
        return agent['callStats']?.['totalCalls'] > 0 ? 'active' : 'inactive';
      }))];
      setAgentStatuses(statuses.sort());
    }
  }, [allAgents]);

  // Filter agents based on search and type
  useEffect(() => {
    let filtered = allAgents || [];

    // Apply search filter with null checks
    if (searchText && searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(agent => {
        if (!agent) return false;
        return (
          agent['name']?.toLowerCase().includes(search) ||
          agent['script']?.toLowerCase().includes(search) ||
          agent['scriptSummary']?.toLowerCase().includes(search) ||
          agent['phone']?.toLowerCase().includes(search) ||
          (agent['tags'] && Array.isArray(agent['tags']) && agent['tags'].some(tag => {
            const tagName = typeof tag === 'string' ? tag : tag?.name;
            return tagName && tagName.toLowerCase().includes(search);
          }))
        );
      });
    }

    // Apply type filter with null check
    if (selectedType && selectedType !== 'all') {
      filtered = filtered.filter(agent => agent && agent['type'] === selectedType);
    }

    // Apply status filter with null checks
    const selectedStatusList = selectedValues ? Object.keys(selectedValues).filter(key => selectedValues[key]) : [];
    if (selectedStatusList.length > 0) {
      filtered = filtered.filter(agent => {
        if (!agent) return false;
        const status = agent['callStats']?.['totalCalls'] > 0 ? 'active' : 'inactive';
        return selectedStatusList.includes(status);
      });
    }

    // Apply tag filter with proper null checks
    if (selectedTagsList && selectedTagsList.length > 0) {
      filtered = filtered.filter(agent => {
        if (!agent || !agent['tags'] || !Array.isArray(agent['tags']) || agent['tags'].length === 0) {
          return false;
        }
        return selectedTagsList.some(selectedTag => {
          if (!selectedTag) return false;
          return agent['tags'].some(agentTag => {
            if (!agentTag) return false;
            const tagName = typeof agentTag === 'string' ? agentTag : agentTag?.name;
            return tagName === selectedTag;
          });
        });
      });
    }

    setFilteredContacts(filtered);
  }, [searchText, selectedType, allAgents, selectedValues, selectedTagsList]);

  const toggleNewAgentModal = () => dispatch(setNewAgentModal(!newAgentModal));

  const dataAgents = filteredContacts?.map((item, index) => {
    const handleRowClick = () => {
      document.body.classList.add("hide-layout");
      history.push(getWorkspacePath(`/agents/${item['_id']}`, currentWorkspace?._id)); // save state
    };
    // Compute status based on call activity
    const status = item['callStats']?.['totalCalls'] > 0 ? 'active' : 'inactive';
    return {
      _id: item['_id'],
      agentName: (
        <div className='agent-name-box' onClick={handleRowClick}>
          <div className='img-box'>
            <img src={item?.image || DefaultAvatar} alt="Agent" className='agent-avatar' />
            <span className={`status-agent ${status === 'active' ? 'green' : 'grey'}`}></span>
          </div>
          <h4>{item['name'] || 'N/A'}</h4>
          <div className='content-area' onClick={(e) => e.stopPropagation()}>
            <Dropdown className="create-btn dropdwon-email" isOpen={openDropdownId === index} toggle={() => setOpenDropdownId(openDropdownId === index ? null : index)}>
              <DropdownToggle>
                <svg width="20px" height="20px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="#4b5563" class="bi bi-three-dots-vertical">
                  <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
                </svg>
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => dispatch(cloneAgent(item['_id']))}>Clone</DropdownItem>
                <DropdownItem onClick={() => setDelete(item['_id'])}>Delete</DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      ),
      agentType: <div className='agent-type' onClick={handleRowClick}> {item['type'] || 'N/A'} </div>,
      agentTask: <div className='agent-type' onClick={handleRowClick}> {item['callStats']?.['totalCalls'] || 0} Calls </div>,
      phoneAgent: (
        <div className='agent-type' onClick={handleRowClick} style={{ display: 'flex', flexDirection: 'column', gap: '5px', padding: '6px 0' }}>
          {item['inboundPhone'] && (
            <div style={{ backgroundColor: '#E8F4FD', color: '#1E48B4', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '500', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', width: 'fit-content' }}><span style={{ marginRight: '5px', fontSize: '14px' }}>↓</span>{item['inboundPhone']}</div>
          )}
          {item['outboundPhone'] && (
            <div style={{ backgroundColor: '#FEF3E2', color: '#D97706', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '500', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', width: 'fit-content' }}><span style={{ marginRight: '5px', fontSize: '14px' }}>↑</span>{item['outboundPhone']}</div>
          )}
          {!item['inboundPhone'] && !item['outboundPhone'] && <span style={{ color: '#999' }}>N/A</span>}
        </div>
      ),
      voiceAgent: (<div className='voice-box' onClick={handleRowClick}> <span className='img-box'> <img src={item['voiceId']?.['voiceImg']} alt="Voice Icon" /> </span> {item['voiceId']?.['voiceName'] || 'N/A'} </div>),
      toolsAgent: (
        <div className='tools-box' onClick={handleRowClick}>
          <ul>
            <li><a href='https://google.com/' target="_blank" rel="noopener noreferrer"><svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.9694 6.11766C10.9694 5.7506 10.9365 5.39766 10.8753 5.05884H6V7.0612H8.78589C8.66589 7.70826 8.30118 8.25649 7.75295 8.62355V9.92238H9.42589C10.4047 9.0212 10.9694 7.69414 10.9694 6.11766Z" fill="#4285F4" /><path fill-rule="evenodd" clip-rule="evenodd" d="M5.99967 11.1766C7.39732 11.1766 8.56909 10.7131 9.42556 9.92247L7.75262 8.62365C7.28909 8.93423 6.69614 9.11776 5.99967 9.11776C4.65143 9.11776 3.51025 8.20717 3.10319 6.98364H1.37378V8.32482C2.22555 10.0166 3.97614 11.1766 5.99967 11.1766Z" fill="#34A853" /><path fill-rule="evenodd" clip-rule="evenodd" d="M3.10349 6.98354C2.99996 6.67295 2.94114 6.34118 2.94114 6C2.94114 5.65883 2.99996 5.32706 3.10349 5.01647V3.67529H1.37408C1.02349 4.37412 0.823486 5.16471 0.823486 6C0.823486 6.8353 1.02349 7.62589 1.37408 8.32472L3.10349 6.98354Z" fill="#FBBC05" /><path fill-rule="evenodd" clip-rule="evenodd" d="M5.99967 2.88231C6.75967 2.88231 7.44203 3.14349 7.9785 3.65643L9.46321 2.17172C8.56674 1.33643 7.39497 0.823486 5.99967 0.823486C3.97614 0.823486 2.22555 1.98349 1.37378 3.67526L3.10319 5.01644C3.51025 3.7929 4.65143 2.88231 5.99967 2.88231Z" fill="#EA4335" /></svg></a></li>
            <li><a href='https://gmail.com/' target="_blank" rel="noopener noreferrer"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_2063_5275)"><path d="M1.41189 11.8555H3.47072V6.85554L0.529541 4.64966V10.9732C0.529541 11.4614 0.925129 11.8555 1.41189 11.8555Z" fill="#4285F4" /><path d="M10.5295 11.8555H12.5884C13.0766 11.8555 13.4707 11.46 13.4707 10.9732V4.64966L10.5295 6.85554" fill="#34A853" /><path d="M10.5295 3.03207V6.8556L13.4707 4.64972V3.47325C13.4707 2.38207 12.2251 1.76001 11.3531 2.41443" fill="#FBBC04" /><path d="M3.4707 6.85551V3.03198L7.00012 5.67904L10.5295 3.03198V6.85551L7.00012 9.50257" fill="#EA4335" /><path d="M0.529541 3.47325V4.64972L3.47072 6.8556V3.03207L2.64719 2.41443C1.77366 1.76001 0.529541 2.38207 0.529541 3.47325" fill="#C5221F" /></g><defs><clipPath id="clip0_2063_5275"><rect width="12.9412" height="12.9412" fill="white" transform="translate(0.529541 0.529541)" /></clipPath></defs></svg></a></li>
            <li><a href='https://hubspot.com/' target="_blank" rel="noopener noreferrer"><svg width="9" height="11" viewBox="0 0 9 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.27245 7.18027L3.75805 7.07002C3.76819 7.06772 3.78535 7.0639 3.80564 7.05739C3.93976 7.02209 4.05469 6.93707 4.12622 6.82025C4.19774 6.70342 4.22026 6.56391 4.18905 6.4311L4.18709 6.42268C4.17104 6.35905 4.143 6.29893 4.10441 6.24543C4.05003 6.17769 3.98273 6.121 3.90626 6.07852C3.81624 6.02818 3.72204 5.98541 3.62466 5.95066L3.09225 5.75964C2.79348 5.65054 2.49471 5.54449 2.19282 5.44075C1.99741 5.37261 1.83165 5.31251 1.68772 5.26848C1.66033 5.26104 1.63314 5.25287 1.6062 5.24398C1.43224 5.19192 1.30977 5.1701 1.20641 5.16933C1.13733 5.16681 1.06852 5.17909 1.00476 5.20532C0.938151 5.23362 0.87835 5.27536 0.829243 5.32782C0.804881 5.35497 0.781967 5.38333 0.760595 5.41281C0.719042 5.47546 0.684766 5.54249 0.658406 5.61264C0.559491 5.89938 0.510185 6.20035 0.512531 6.50307C0.514481 6.7764 0.521892 7.12744 0.675178 7.36556C0.712231 7.42681 0.761767 7.47964 0.820663 7.5206C0.929874 7.59448 1.03986 7.60443 1.15493 7.61247C1.32654 7.62434 1.49231 7.58338 1.65808 7.54548L3.27128 7.17989L3.27245 7.18027ZM8.6901 4.65023C8.55755 4.37715 8.3793 4.1278 8.16277 3.91254C8.13479 3.88517 8.1048 3.85983 8.07306 3.83675C8.04373 3.81516 8.01327 3.79509 7.98179 3.77664C7.94938 3.75922 7.91605 3.7435 7.88194 3.72956C7.8146 3.70357 7.74243 3.69183 7.67015 3.6951C7.60111 3.69893 7.53364 3.71807 7.4724 3.74985C7.37996 3.79502 7.27972 3.86775 7.14633 3.98911C7.12799 4.0071 7.10498 4.02739 7.08392 4.04653C6.97432 4.14798 6.85184 4.27316 6.70636 4.41863C6.48131 4.64181 6.26015 4.86614 6.03978 5.09239L5.64584 5.49358C5.57391 5.56675 5.50841 5.64577 5.45004 5.72978C5.40007 5.80087 5.3647 5.88084 5.3459 5.96521C5.33535 6.03003 5.33694 6.09619 5.35058 6.16045L5.35253 6.16887C5.38366 6.30154 5.46603 6.41717 5.58229 6.49143C5.69854 6.56569 5.83962 6.59278 5.97582 6.567C5.99221 6.56473 6.00848 6.56167 6.02457 6.55781L8.12377 6.08159C8.28953 6.04407 8.45725 6.00962 8.60585 5.92463C8.7057 5.86836 8.80048 5.81208 8.86562 5.69877C8.90032 5.63636 8.92131 5.5675 8.92725 5.49664C8.95962 5.21565 8.81023 4.89677 8.6901 4.65023ZM4.93207 5.51655C5.08419 5.32897 5.08419 5.04951 5.09745 4.82059C5.14347 4.05687 5.19145 3.29315 5.23006 2.52904C5.24449 2.23963 5.27609 1.95405 5.25853 1.66234C5.2441 1.42193 5.24254 1.14554 5.0877 0.948006C4.81467 0.599642 4.23195 0.628354 3.8345 0.682714C3.71189 0.699336 3.59019 0.721821 3.46981 0.75009C3.3489 0.778418 3.22877 0.809426 3.11176 0.84656C2.7303 0.969061 2.19399 1.19416 2.1035 1.62559C2.05202 1.86945 2.17371 2.11866 2.26732 2.34146C2.3816 2.61096 2.53723 2.85405 2.6792 3.10786C3.05481 3.77818 3.43705 4.44466 3.81812 5.11153C3.9324 5.31059 4.05604 5.56249 4.27641 5.66585C4.29124 5.67197 4.30606 5.67733 4.32127 5.68231C4.41995 5.71906 4.52799 5.72595 4.63057 5.7026L4.6489 5.69877C4.74407 5.67312 4.82988 5.62221 4.89736 5.55215C4.90984 5.54067 4.92076 5.52918 4.93207 5.51655ZM4.74992 7.56118C4.69055 7.47921 4.60629 7.41778 4.50919 7.38566C4.41209 7.35355 4.30713 7.35241 4.20933 7.3824C4.18632 7.38984 4.1639 7.39892 4.14224 7.40958C4.10872 7.42636 4.07675 7.44596 4.04668 7.46815C3.96082 7.53456 3.88412 7.61164 3.81851 7.69746C3.80173 7.71851 3.78613 7.74646 3.76585 7.76483L3.42808 8.22039C3.23696 8.47611 3.04779 8.73221 2.86096 8.99215C2.73849 9.1602 2.63318 9.30223 2.54932 9.42741C2.53372 9.45115 2.51734 9.47718 2.50251 9.49861C2.40227 9.65021 2.34572 9.76123 2.31646 9.85999C2.29461 9.92561 2.28769 9.99513 2.29618 10.0637C2.30554 10.1349 2.33012 10.2034 2.36795 10.265C2.38823 10.2956 2.41007 10.3255 2.43348 10.3542C2.48402 10.4113 2.54094 10.4627 2.60314 10.5073C2.83639 10.6666 3.09147 10.7811 3.35982 10.8695C3.58331 10.9422 3.81539 10.9859 4.05019 10.9989C4.09036 11.0008 4.13054 11 4.17032 10.9966C4.20721 10.9932 4.24392 10.9881 4.28031 10.9813C4.31673 10.9729 4.35266 10.9627 4.38796 10.9506C4.4566 10.9254 4.51906 10.8863 4.57128 10.8358C4.62082 10.7872 4.65904 10.7286 4.68284 10.6639C4.72184 10.5682 4.74758 10.4472 4.76474 10.2673C4.76591 10.2417 4.76981 10.211 4.77254 10.1831C4.7858 10.0338 4.79205 9.8577 4.80141 9.65174C4.81818 9.33439 4.83105 9.01856 4.84119 8.70197L4.86303 8.13846C4.8681 8.00907 4.86381 7.86513 4.82715 7.73612C4.81103 7.67415 4.78496 7.6151 4.74992 7.56118ZM8.56178 8.4428C8.49157 8.36662 8.39172 8.29121 8.23414 8.1978C8.21152 8.18517 8.18461 8.16871 8.16004 8.15416C8.02899 8.07645 7.87102 7.99529 7.68653 7.89691C7.40297 7.74378 7.11941 7.59525 6.83273 7.44748L6.32685 7.1841C6.30072 7.17644 6.2742 7.15807 6.24923 7.1462C6.15196 7.09743 6.04865 7.06128 5.94188 7.03863C5.9051 7.03166 5.86778 7.02782 5.83033 7.02715C5.80608 7.02702 5.78185 7.02843 5.75778 7.03136C5.65651 7.04671 5.56276 7.09306 5.48992 7.1638C5.41708 7.23453 5.36886 7.32605 5.35214 7.42528C5.34473 7.48844 5.34707 7.55237 5.35955 7.61439C5.38413 7.74684 5.44419 7.87738 5.5066 7.99185L5.77651 8.48913C5.92706 8.76935 6.07918 9.04804 6.23519 9.32596C6.33582 9.50704 6.41968 9.66208 6.49808 9.79071C6.5129 9.81482 6.52967 9.84085 6.54254 9.86344C6.6381 10.0177 6.71455 10.115 6.79217 10.185C6.84387 10.2338 6.90593 10.2708 6.9739 10.2933C7.04187 10.3158 7.11407 10.3233 7.18533 10.3152C7.22245 10.3107 7.25929 10.3043 7.29571 10.296C7.37032 10.2764 7.44243 10.2485 7.51062 10.213C7.71592 10.1 7.90516 9.96099 8.07345 9.79951C8.27549 9.60427 8.45413 9.39143 8.59298 9.14795C8.61249 9.1135 8.62887 9.0779 8.64291 9.04077C8.65598 9.00676 8.6673 8.97214 8.67684 8.93702C8.68503 8.90104 8.69127 8.86505 8.69556 8.82868C8.70256 8.75721 8.69419 8.68509 8.67099 8.61699C8.64809 8.55163 8.6108 8.49241 8.56178 8.4428Z" fill="#FF1A1A" /></svg></a></li>
            <li><a href='https://linkedin.com/' target="_blank" rel="noopener noreferrer"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.1529 11.1764H0.952896V4.2529H3.1529V11.1764ZM2.0529 3.28231C1.34113 3.28231 0.823486 2.76466 0.823486 2.0529C0.823486 1.34113 1.40584 0.823486 2.0529 0.823486C2.76466 0.823486 3.28231 1.34113 3.28231 2.0529C3.28231 2.76466 2.76466 3.28231 2.0529 3.28231ZM11.1764 11.1764H8.97643V7.42349C8.97643 6.32349 8.52348 5.99996 7.87642 5.99996C7.22936 5.99996 6.58231 6.5176 6.58231 7.48819V11.1764H4.38231V4.2529H6.4529V5.22349C6.64701 4.77055 7.42348 4.05878 8.52348 4.05878C9.75289 4.05878 11.047 4.77055 11.047 6.90584V11.1764H11.1764Z" fill="#2867B2" /></svg></a></li>
          </ul>
          <span>+2</span>
        </div>
      ),
      updatedAt: moment(item['updatedAt']).format('lll'),
      agentTags: (() => {
        if (!item['tags'] || !Array.isArray(item['tags']) || item['tags'].length === 0) {
          return <span style={{ color: '#999' }}>No tags</span>;
        }

        const validTags = item['tags'].filter(tag => tag && (typeof tag === 'string' || tag?.name));
        if (validTags.length === 0) {
          return <span style={{ color: '#999' }}>No tags</span>;
        }

        const firstTag = validTags[0];
        const firstTagName = typeof firstTag === 'string' ? firstTag : firstTag?.name;
        const firstTagColor = typeof firstTag === 'string' ? '#6B7280' : (firstTag?.color || '#6B7280');
        const remainingCount = validTags.length - 1;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span
              style={{
                backgroundColor: `${firstTagColor}20`, // 20% opacity for background
                color: firstTagColor,
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                display: 'inline-block',
                border: `1px solid ${firstTagColor}40`, // 40% opacity for border
                whiteSpace: 'nowrap'
              }}
            >
              {firstTagName}
            </span>
            {remainingCount > 0 && (
              <span
                style={{
                  color: '#000000',
                  fontSize: '12px',
                  display: 'inline-block',
                  whiteSpace: 'nowrap'
                }}
              >
                +{remainingCount}
              </span>
            )}
          </div>
        );
      })(),
      agentImprovement: (() => {
        const suggestion = item['improvementSuggestions']?.find(s => s['priority'] === 'high') ||
          item['improvementSuggestions']?.[0] ||
          { message: 'Performing well', priority: 'low' };

        const iconColor = suggestion['priority'] === 'high' ? '#dc3545' :
          suggestion['priority'] === 'medium' ? '#ffc107' : '#28a745';

        return (
          <div
            onClick={handleRowClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px'
            }}
          >
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="4" r="4" fill={iconColor} />
            </svg>
            {suggestion['message']}
          </div>
        );
      })(),
      rowClick: (history) => () => history.push(getWorkspacePath(`/agents/${item['_id']}`, currentWorkspace?._id)),
    };
  }) || [];


  // set selected rows
  const handleSelectedRowsChange = ({ selectedRows }) => { setSelectedRows(selectedRows); };

  // handle checkbox toggle for box view
  const handleBoxViewSelect = (agentId) => {
    const agent = filteredContacts.find(a => a['_id'] === agentId);
    if (!agent) return;

    const isSelected = selectedRows.some(row => row['_id'] === agentId);
    if (isSelected) setSelectedRows(selectedRows.filter(row => row['_id'] !== agentId));
    else setSelectedRows([...selectedRows, agent]);
  };

  // table stats
  const isPaginated = dataAgents.length > 100;
  const headerScrollHeight = isPaginated ? 'calc(100vh - 189px)' : 'calc(100vh - 161px)';
  const responsiveClass = isPaginated ? 'datatable--paginated' : 'datatable--fullheight contact-table';

  // Check if there are no agents at all (not just filtered out)
  const hasNoAgents = !isLoading && allAgents && allAgents.length === 0;

  return (
    <>
      <div className='agents-page'>
        {/* Hero Header */}
        <div className='agents-hero-header'>
          <div className='hero-left'>
            <h1 className='hero-title'>AI Assistants</h1>
            <div className='agents-hero-meta'>
              <span className='meta-chip'>{allAgents?.length || 0} Total</span>
              <span className='meta-chip active'>{allAgents?.filter(a => a?.callStats?.totalCalls > 0).length || 0} Active</span>
              <span className='meta-chip inactive'>{allAgents?.filter(a => !(a?.callStats?.totalCalls > 0)).length || 0} Inactive</span>
            </div>
          </div>
          <div className='hero-right'>
            <button className='btn-style-new' onClick={toggleNewAgentModal}>+ New Agent</button>
          </div>
        </div>

        {hasNoAgents ? <EmptyAgentState /> : (
          <>
            {/* Bulk or Filter Bar */}
            {selectedRows.length > 0 ? (
              <div className='agents-bulk-bar'>
                <div className='bulk-left'>
                  <p className='bulk-count'>{selectedRows.length} agent{selectedRows.length > 1 ? 's' : ''} selected</p>
                  {selectedRows.length <= 1 && (
                    <>
                      <button className='bulk-action-btn' onClick={() => { dispatch(cloneAgent(selectedRows[0]['_id'])); setSelectedRows([]); }}>Clone</button>
                      <button className='bulk-action-btn' onClick={() => setDelete(selectedRows[0]['_id'])}>Delete</button>
                    </>
                  )}
                  <button className='bulk-action-btn' onClick={handleOpenTagsManager}>
                    Add Tags
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl && anchorEl.tagName)} onClose={handleCloseTagsManager}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      <TagsManager objectType="agents" mode="select" selectedObjects={selectedRows.map(row => row._id || row)}
                        onClose={handleCloseTagsManager}
                        onTagsApplied={() => { setAnchorEl(null); setTagsManagerOpen(false); setSelectedRows([]); dispatch(getAllAgents()); }}
                      />
                    </Menu>
                  </button>
                </div>
                <button className='bulk-clear-btn' onClick={() => { setToggleCleared(prev => !prev); setSelectedRows([]); }}>✕</button>
              </div>
            ) : (
              <div className='agents-filter-bar'>
                <div className='afb-left'>
                  <div className='afb-search'>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{color:'rgba(255,255,255,0.4)', flexShrink:0}}>
                      <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="currentColor" stroke="currentColor" strokeWidth="0.236842" />
                      <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="currentColor" stroke="currentColor" strokeWidth="0.236842" />
                    </svg>
                    <TextField fullWidth hiddenLabel size="small" name='search' variant="standard"
                      placeholder='Search agents...' value={searchText}
                      onChange={(e) => setSearchText(e['target']['value'])}
                    />
                  </div>
                  {agentStatuses.length > 1 && (
                    <button className='afb-filter-btn' onClick={handleClick}>
                      {Object.keys(selectedValues).filter(k => selectedValues[k]).length > 0
                        ? `Status (${Object.keys(selectedValues).filter(k => selectedValues[k]).length})`
                        : 'Status'} ▾
                    </button>
                  )}
                  {allTags.length > 0 && (
                    <button className='afb-filter-btn' onClick={handleClickTagFilter}>
                      {selectedTagsList.length > 0 ? `Tags (${selectedTagsList.length})` : 'Tags'} ▾
                    </button>
                  )}
                </div>
                <div className='afb-right'>
                  <button className={`afb-view-btn ${!isTableView ? 'active' : ''}`} onClick={() => setTableView(false)}>Grid</button>
                  <button className={`afb-view-btn ${isTableView ? 'active' : ''}`} onClick={() => setTableView(true)}>List</button>
                </div>
              </div>
            )}

            {/* Status filter menu */}
            <Menu anchorEl={statusDropdownAnchor} open={Boolean(statusDropdownAnchor)} onClose={handleClose}>
              {agentStatuses.length === 0 ? (
                <MenuItem disabled><ListItemText primary="No status options available" /></MenuItem>
              ) : (
                agentStatuses.map((status) => (
                  <MenuItem key={status} onClick={() => toggleValue(status)}>
                    <Checkbox checked={!!selectedValues[status]} />
                    <ListItemText primary={status === 'active' ? 'Active' : 'Inactive'} />
                  </MenuItem>
                ))
              )}
            </Menu>

            <div className='agents-grid-container'>
              {isLoading ? (
                <div style={{ padding: '60px', textAlign: 'center' }}><TableAnimationFiles /></div>
              ) : filteredContacts.length === 0 ? (
                <div className='studio-empty'>No agents found matching your search.</div>
              ) : isTableView ? (
                /* LIST VIEW */
                <div className='agent-studio-list'>
                  {filteredContacts.map((item, index) => {
                    const status = item?.callStats?.totalCalls > 0 ? 'active' : 'inactive';
                    const handleRowClick = () => {
                      document.body.classList.add("hide-layout");
                      history.push(getWorkspacePath(`/agents/${item['_id']}`, currentWorkspace?._id));
                    };
                    return (
                      <div key={item['_id']} className='agent-studio-list-row' onClick={handleRowClick}>
                        <div className={`asl-stripe ${status}`}></div>
                        <div className='asl-avatar-wrap'>
                          <img src={item?.image || DefaultAvatar} className='asl-avatar' alt='' />
                          <span className={`asl-status-dot ${status}`}></span>
                        </div>
                        <div className='asl-name-col'>
                          <span className='asl-name'>{item['name'] || 'N/A'}</span>
                          {item['type'] && <span className='asl-badge'>{item['type']}</span>}
                        </div>
                        <div className='asl-voice-col'>
                          <span className='asl-label'>Voice</span>
                          <span className='asl-value'>{item['voiceId']?.['voiceName'] || 'N/A'}</span>
                        </div>
                        <div className='asl-model-col'>
                          <span className='asl-label'>Model</span>
                          <span className='asl-value'>{item['modelId']?.['label'] || 'N/A'}</span>
                        </div>
                        <div className='asl-phone-col'>
                          <span className='asl-label'>Phone</span>
                          <span className='asl-value'>{item['inboundPhone'] || item['outboundPhone'] || 'N/A'}</span>
                        </div>
                        <div className='asl-stats-col'>
                          {item['callStats']?.['totalCalls'] || 0} calls
                        </div>
                        <div className='asl-actions' onClick={(e) => e.stopPropagation()}>
                          <Dropdown isOpen={openDropdownId === `list-${index}`} toggle={() => setOpenDropdownId(openDropdownId === `list-${index}` ? null : `list-${index}`)}>
                            <DropdownToggle tag="button" className='asl-menu-btn'>⋮</DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => dispatch(cloneAgent(item['_id']))}>Clone</DropdownItem>
                              <DropdownItem onClick={() => setDelete(item['_id'])}>Delete</DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* GRID VIEW */
                <div className='agent-studio-grid'>
                  {filteredContacts.map((item, index) => {
                    const status = item?.callStats?.totalCalls > 0 ? 'active' : 'inactive';
                    const handleCardClick = () => {
                      document.body.classList.add("hide-layout");
                      history.push(getWorkspacePath(`/agents/${item['_id']}`, currentWorkspace?._id));
                    };
                    return (
                      <div key={item['_id']} className='agent-studio-card' onClick={handleCardClick} style={{cursor:'pointer'}}>
                        <div className={`card-stripe ${status}`}></div>
                        <div className='card-body'>
                          <div className='card-top'>
                            <div className='card-avatar-wrap'>
                              <img src={item?.image || DefaultAvatar} className='card-avatar' alt='' />
                              <span className={`card-status-dot ${status}`}></span>
                            </div>
                            <div className='card-title-area'>
                              <h3 className='card-name'>{item['name'] || 'N/A'}</h3>
                              {item['type'] && <span className='card-badge'>{item['type']}</span>}
                            </div>
                            <div onClick={(e) => e.stopPropagation()}>
                              <Dropdown isOpen={openDropdownId === index} toggle={() => setOpenDropdownId(openDropdownId === index ? null : index)}>
                                <DropdownToggle tag="button" className='card-menu-btn'>⋮</DropdownToggle>
                                <DropdownMenu>
                                  <DropdownItem onClick={() => dispatch(cloneAgent(item['_id']))}>Clone</DropdownItem>
                                  <DropdownItem onClick={() => setDelete(item['_id'])}>Delete</DropdownItem>
                                </DropdownMenu>
                              </Dropdown>
                            </div>
                          </div>
                          <div className='card-meta'>
                            <div className='card-meta-row'>
                              <span className='meta-label'>Voice</span>
                              <span className='meta-value'>{item['voiceId']?.['voiceName'] || 'N/A'}</span>
                            </div>
                            <div className='card-meta-row'>
                              <span className='meta-label'>Model</span>
                              <span className='meta-value'>{item['modelId']?.['label'] || 'N/A'}</span>
                            </div>
                          </div>
                          <div className='card-divider'></div>
                          <div className='card-phone'>
                            <span>📞</span>
                            <span>{item['inboundPhone'] || item['outboundPhone'] || 'No phone assigned'}</span>
                          </div>
                          <div className='card-stats'>
                            <span className='stat-item'>{item['callStats']?.['totalCalls'] || 0} calls</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Delete Agent Modal */}
      <Modal isOpen={isDelete} className={`main-modal new-user-modal modal-dialog-centered select-tampltes-modal`}>
        <ModalHeader></ModalHeader>
        <ModalBody>
          <div className='content-area'>
            <div className='delete-modal'>
              <h4>
                <i className='icon'>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z" fill="#FF3B30" fillOpacity="0.15" />
                    <path d="M12 19.5C16.1421 19.5 19.5 16.1421 19.5 12C19.5 7.85786 16.1421 4.5 12 4.5C7.85786 4.5 4.5 7.85786 4.5 12C4.5 16.1421 7.85786 19.5 12 19.5Z" fill="#FF3B30" />
                    <path d="M12 9V12" stroke="#FFE2E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 15H12.0083" stroke="#FFE2E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </i>
                Delete Agent
              </h4>
              <p>Are you sure you want to delete this Agent? You can't undo this action afterwards.</p>
              <div className='form-area'>
                <div className='btns-box text-right'>
                  <button className='btn-style-border' onClick={() => setDelete(null)}>Cancel</button>
                  <button className='btn-style-new' onClick={() => { dispatch(deleteAgent(isDelete)); setDelete(null); setSelectedRows([]); }}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Tags Manager Dialog */}
      {isTagsModal && (
        <Dialog open={isTagsModal} onClose={() => setTagsModal(false)} maxWidth="sm" fullWidth
          slotProps={{ paper: { sx: { borderRadius: '8px', minWidth: '260px', maxWidth: '320px', minHeight: '400px' } } }}
        >
          <TagsManager objectType="agents" mode="select" selectedObjects={selectedRows.map(row => row._id || row)}
            onClose={() => setTagsModal(false)}
            onTagsApplied={() => { setTagsModal(false); setSelectedRows([]); setToggleCleared(prev => !prev); dispatch(getAllAgents()); }}
          />
        </Dialog>
      )}

      {/* Tag Filter Popover */}
      <Popover open={Boolean(tagFilterAnchor)} anchorEl={tagFilterAnchor} onClose={handleCloseTagFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: '8px', minWidth: '300px', maxWidth: '400px', minHeight: '300px', maxHeight: '400px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } } }}
      >
        <TagsManager objectType="agents" mode="filter" selectedTags={selectedTagsList}
          onClose={handleCloseTagFilter} onTagsChange={handleTagsFilterChange}
          onTagsApplied={() => { dispatch(getAllAgents()); }}
        />
      </Popover>
    </>
  );
};

export default Agents;